/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Page, PageHeader, Tabs } from '../../components/console/ui';
import { preferencesService, walletService } from '../../services/api';
import { useWallet } from '../../hooks/useWallet';
import { Notice } from '../../components/billing/BillingModal';
import { DisplayCurrency, formatTimeLeft, money } from '../../components/billing/billingUtils';
import {
  BillingContextValue,
  useExchangeRate,
  useInvalidateWallet,
  useInvoices,
} from '../../components/billing/useBilling';

const SUBTITLES: Record<string, string> = {
  '/billing': 'One wallet pays for QuickPods, TrainPods and Inference.',
  '/billing/transactions': 'Charges grouped so each resource is one line per day.',
  '/billing/invoices': 'Invoices for your billing periods, with status and a PDF download.',
  '/billing/payments': 'Top up the wallet, manage cards and auto-pay.',
};

const BillingLayout: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { wallet } = useWallet();
  const { total: invoiceCount, isSuccess: invoicesLoaded } = useInvoices();
  const exchangeRate = useExchangeRate();
  const invalidate = useInvalidateWallet();

  // Display currency defaults to the user's preference.
  const [currency, setCurrency] = useState<DisplayCurrency>('INR');
  const { data: prefs } = useQuery({
    queryKey: ['appearancePreferences'],
    queryFn: () =>
      preferencesService
        .getAppearance()
        .then((res) => res.data)
        .catch(() => ({ preferred_currency: 'INR' })),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (prefs?.preferred_currency === 'INR' || prefs?.preferred_currency === 'USD') setCurrency(prefs.preferred_currency);
  }, [prefs]);

  // Legacy deep link: /wallet?action=topup → Add funds tab.
  useEffect(() => {
    if (searchParams.get('action') === 'topup' && pathname !== '/billing/payments') {
      navigate('/billing/payments?action=topup', { replace: true });
    }
  }, [searchParams, pathname, navigate]);

  // PayPal / Binance return: ?payment=success&token=<order id> or ?payment=cancelled.
  const processedTokenRef = useRef<string | null>(null);
  const capture = useMutation({
    mutationFn: (orderId: string) => walletService.capturePayPalOrder(orderId),
    onSuccess: (response) => {
      const { amount_credited } = response.data;
      toast.success(`Payment successful! ₹${amount_credited?.toFixed(2) || ''} added to your wallet.`);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to capture PayPal payment');
    },
  });
  const { mutate: captureOrder } = capture;
  useEffect(() => {
    const payment = searchParams.get('payment');
    const token = searchParams.get('token');
    if (payment === 'success' && token) {
      if (processedTokenRef.current === token) return;
      processedTokenRef.current = token;
      setSearchParams({}, { replace: true });
      toast.loading('Processing PayPal payment...', { id: 'paypal-capture' });
      captureOrder(token, {
        onSettled: () => {
          toast.dismiss('paypal-capture');
          processedTokenRef.current = null;
        },
      });
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, captureOrder]);

  const ctx: BillingContextValue = { currency, setCurrency, exchangeRate };

  return (
    <Page>
      <PageHeader
        title="Billing"
        subtitle={SUBTITLES[pathname.replace(/\/$/, '')] ?? SUBTITLES['/billing']}
        actions={
          <div className="flex gap-2 flex-wrap" role="group" aria-label="Display currency">
            {(['INR', 'USD'] as const).map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={currency === c}
                onClick={() => setCurrency(c)}
                className={`ps-btn2 ${currency === c ? '!border-ps-accent !text-ps-accent' : ''}`}
              >
                {c === 'INR' ? '₹ INR' : '$ USD'}
              </button>
            ))}
          </div>
        }
      />

      {wallet?.services_stopped_for_balance ? (
        <Notice tone="err">
          <b className="font-medium">Services stopped due to insufficient balance.</b> Your running services were stopped
          because the wallet couldn’t cover their hourly cost. Add funds to restart them.{' '}
          <button type="button" className="underline font-medium" onClick={() => navigate('/billing/payments?action=topup')}>
            Add funds
          </button>
        </Notice>
      ) : wallet?.is_low_balance ? (
        <Notice>
          <b className="font-medium">Low balance.</b> Your balance ({money(wallet.available_balance || 0, currency, exchangeRate)})
          is below your hourly infrastructure cost ({money(wallet.hourly_run_rate || 0, currency, exchangeRate)}/hr)
          {wallet.estimated_hours_left > 0 ? `, about ${formatTimeLeft(wallet.estimated_hours_left)} left` : ''}.
          {wallet.low_balance_notification_count > 0 &&
            ` You’ve received ${wallet.low_balance_notification_count} low-balance notification${
              wallet.low_balance_notification_count > 1 ? 's' : ''
            }.`}{' '}
          Services may be stopped automatically if you don’t add funds.{' '}
          <button type="button" className="underline font-medium" onClick={() => navigate('/billing/payments?action=topup')}>
            Add funds
          </button>
        </Notice>
      ) : null}

      <Tabs
        label="Billing sections"
        tabs={[
          { label: 'Overview', to: '/billing', end: true },
          { label: 'Transactions', to: '/billing/transactions' },
          { label: 'Invoices', to: '/billing/invoices', badge: invoicesLoaded ? invoiceCount : undefined },
          { label: 'Add funds & cards', to: '/billing/payments' },
        ]}
      />
      <Outlet context={ctx} />
    </Page>
  );
};

export default BillingLayout;
